import * as React from 'react';
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import {queryOptions, useSuspenseQuery, useQueryClient, useMutation} from '@tanstack/react-query';
import {getCategories, syncProducts, syncQuerySuggestions} from '@/web/lib/services';
import type { Category } from '@/types';
import { LoadingScreen } from '@/web/components/molecules/LoadingScreen.tsx';
import { CategoriesNavCard } from '@/web/components/molecules/CategoriesNavCard';
import {Button} from "@/web/components/atoms/button.tsx";
import {RiLoopLeftLine} from "react-icons/ri";

export const Route = createFileRoute('/products')({
  loader: async ({ context }) => {
    return context.queryClient?.ensureQueryData(categoriesQueryOptions);
  },
  preload: true,
  component: Products,
  pendingComponent: LoadingScreen
});

const CATEGORIES_QUERY_KEY = ['products'];

const categoriesQueryOptions = queryOptions({
  queryKey: CATEGORIES_QUERY_KEY,
  queryFn: getCategories,
  staleTime: 1000 * 60 * 5,
});

function Products() {
  const [selectedCategory, setSelectedCategory] = React.useState<Category | null>(null);
  const { error, data } = useSuspenseQuery(categoriesQueryOptions);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  if (!data) {
    return <div>No data</div>;
  }

  const handleSelectCategory = (category: Category) => () => {
    navigate({ to: `/products/${category.id}/all` });
  }

  const syncProductsMutation = useMutation({
    mutationFn: syncProducts,
    onSuccess: () => {
      console.log('Successfully synced products');
    },
  });

  const handleProductsSync = () => {
    syncProductsMutation.mutate();
  }

  const { categories } = data;
  return <div className="main-content mt-24">
    <div className="p-4 w-full overflow-hidden">
      <div className="flex w-full max-w-full">
        <div className="w-[272px] flex-shrink-0">
          <CategoriesNavCard categories={categories} onSelectCategory={handleSelectCategory} selectedCategory={selectedCategory} />
          <Button variant="outline" className="mt-4 w-full" onClick={handleProductsSync}><RiLoopLeftLine />Sync Products</Button>
        </div>
        <div className="flex-1 w-full px-8 min-w-0">
          <Outlet />
        </div>
      </div>
    </div>
  </div>;
}

export default Products;
